/**
 * Campaign Actions Service
 * Handles API calls for campaign pause/resume/scale actions
 */

const API_BASE = 'http://localhost:3001';

/**
 * Pause a campaign
 * @param {string} campaignId - Platform campaign ID
 * @param {string} platform - Platform name (meta, google, etc.)
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<Object>} - Result of the operation
 */
export const pauseCampaign = async (campaignId, platform, userId, brandId) => {
    const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, userId, brandId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to pause campaign');
    }

    return response.json();
};

/**
 * Resume (activate) a campaign
 * @param {string} campaignId - Platform campaign ID
 * @param {string} platform - Platform name (meta, google, etc.)
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<Object>} - Result of the operation
 */
export const resumeCampaign = async (campaignId, platform, userId, brandId) => {
    const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, userId, brandId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to resume campaign');
    }

    return response.json();
};

/**
 * Update campaign budget
 * @param {string} campaignId - Platform campaign ID
 * @param {string} platform - Platform name (meta, google, etc.)
 * @param {number} amount - New budget amount in dollars
 * @param {string} budgetType - 'daily' or 'lifetime'
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<Object>} - Result of the operation
 */
export const updateCampaignBudget = async (campaignId, platform, amount, budgetType, userId, brandId) => {
    const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}/budget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, amount, budgetType, userId, brandId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update budget');
    }

    return response.json();
};

/**
 * Create a new campaign
 * @param {Object} config - Campaign configuration
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<Object>} - Created campaign details
 */
export const createCampaign = async (config, userId, brandId) => {
    const response = await fetch(`${API_BASE}/api/campaigns/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...config, userId, brandId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create campaign');
    }

    return response.json();
};

/**
 * Validate campaign configuration before creation
 * @param {Object} config - Campaign configuration
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<Object>} - Validation result
 */
export const validateCampaign = async (config, userId, brandId) => {
    const response = await fetch(`${API_BASE}/api/campaigns/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...config, userId, brandId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Validation failed');
    }

    return response.json();
};

/**
 * Save campaign as draft
 * @param {Object} config - Campaign configuration
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<Object>} - Saved draft details
 */
export const saveCampaignDraft = async (config, userId, brandId) => {
    const response = await fetch(`${API_BASE}/api/campaigns/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...config, userId, brandId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save draft');
    }

    return response.json();
};

/**
 * Get projected performance for campaign settings
 * @param {Object} config - Campaign configuration
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<Object>} - Projected performance metrics
 */
export const getProjectedPerformance = async (config, userId, brandId) => {
    const params = new URLSearchParams({ userId, brandId });

    const response = await fetch(`${API_BASE}/api/campaigns/preview?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get projected performance');
    }

    return response.json();
};

export default {
    pauseCampaign,
    resumeCampaign,
    updateCampaignBudget,
    createCampaign,
    validateCampaign,
    saveCampaignDraft,
    getProjectedPerformance
};
